<template>
  <div class="boletos-lista">
    <div class="page-header">
      <h1 class="page-title">
        <i class="fas fa-barcode"></i>
        Gestão de Boletos
      </h1>
      <p class="page-subtitle">Gerencie os boletos de pagamento das inscrições</p>
    </div>

    <!-- Estatísticas -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon total">
          <i class="fas fa-file-invoice-dollar"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.total || 0 }}</h3>
          <p>Total de Boletos</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon pending">
          <i class="fas fa-clock"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.emitidos || 0 }}</h3>
          <p>Emitidos</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon approved">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.pagos || 0 }}</h3>
          <p>Pagos</p>
        </div>
      </div>

      <div class="stat-card">
        <div class="stat-icon denied">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="stat-content">
          <h3>{{ estatisticas.vencidos || 0 }}</h3>
          <p>Vencidos</p>
        </div>
      </div>

      <div class="stat-card total-value">
        <div class="stat-icon money">
          <i class="fas fa-dollar-sign"></i>
        </div>
        <div class="stat-content">
          <h3>{{ formatarMoeda(estatisticas.valor_total || 0) }}</h3>
          <p>Valor Total</p>
        </div>
      </div>
    </div>

    <!-- Ações Rápidas -->
    <div class="actions-card">
      <h3 class="actions-title">
        <i class="fas fa-cogs"></i>
        Ações Rápidas
      </h3>
      <div class="actions-row">
        <div class="action-group">
          <label>Concurso</label>
          <select v-model="acaoConcursoId" class="form-control">
            <option value="">Selecione um concurso</option>
            <option v-for="concurso in concursos" :key="concurso.id" :value="concurso.id">
              {{ concurso.titulo || concurso.prefeitura }}
            </option>
          </select>
        </div>

        <button 
          @click="atualizarPagamentos" 
          class="btn btn-primary"
          :disabled="!acaoConcursoId || atualizandoPagamentos"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': atualizandoPagamentos }"></i>
          {{ atualizandoPagamentos ? 'Atualizando...' : 'Atualizar Pagamentos BB' }}
        </button>

        <button 
          @click="abrirModalProrrogar" 
          class="btn btn-warning"
          :disabled="!acaoConcursoId"
        >
          <i class="fas fa-calendar-alt"></i>
          Prorrogar Vencimento
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="filters-row">
        <div class="filter-group">
          <label>Concurso</label>
          <select v-model="filters.concurso_id" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option v-for="concurso in concursos" :key="concurso.id" :value="concurso.id">
              {{ concurso.titulo || concurso.prefeitura }}
            </option>
          </select>
        </div>

        <div class="filter-group">
          <label>Status</label>
          <select v-model="filters.status" class="form-control" @change="applyFilters">
            <option value="">Todos</option>
            <option value="Emitido">Emitido</option>
            <option value="Pago">Pago</option>
            <option value="Vencido">Vencido</option>
            <option value="Cancelado">Cancelado</option>
          </select>
        </div>

        <div class="filter-group">
          <label>Candidato</label>
          <input 
            v-model="filters.candidato" 
            type="text" 
            placeholder="Nome ou CPF"
            class="form-control"
            @input="debounceSearch"
          />
        </div>

        <div class="filter-actions">
          <button @click="clearFilters" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Limpar
          </button>
          <button @click="carregarBoletos" class="btn btn-primary">
            <i class="fas fa-sync-alt"></i>
            Atualizar
          </button>
        </div>
      </div>
    </div>

    <!-- Tabela de Boletos -->
    <div class="table-card">
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>Carregando boletos...</p>
      </div>

      <div v-else-if="boletos.length === 0" class="empty-state">
        <i class="fas fa-barcode"></i>
        <h3>Nenhum boleto encontrado</h3>
        <p v-if="filters.candidato || filters.concurso_id || filters.status">
          Não há boletos que correspondam aos filtros aplicados.
        </p>
        <p v-else>
          Nenhum boleto cadastrado no sistema.
        </p>
      </div>

      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th style="width: 80px">ID</th>
                <th style="width: 100px">ID Inscrição</th>
                <th>Candidato</th>
                <th>Concurso</th>
                <th>Vaga</th>
                <th style="width: 120px">Valor</th>
                <th style="width: 120px">Vencimento</th>
                <th style="width: 130px">Status</th>
                <th style="width: 120px">Data Pagamento</th>
                <th style="width: 140px" class="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="boleto in boletos" :key="boleto.id">
                <td><strong>#{{ boleto.id }}</strong></td>
                <td><strong>#{{ boleto.inscricao_id || boleto.inscricao?.id || 'N/A' }}</strong></td>
                <td>
                  <div class="user-info">
                    <strong>{{ boleto.inscricao?.cadastro?.nome || 'N/A' }}</strong>
                    <small>{{ formatarCPF(boleto.inscricao?.cadastro?.cpf) }}</small>
                  </div>
                </td>
                <td>
                  <div class="concurso-info">
                    <strong>{{ boleto.inscricao?.concurso?.prefeitura || 'N/A' }}</strong>
                    <small>{{ boleto.inscricao?.concurso?.titulo }}</small>
                  </div>
                </td>
                <td>{{ boleto.inscricao?.vaga?.cargo || 'N/A' }}</td>
                <td>
                  <span class="valor-badge">
                    R$ {{ formatarMoeda(boleto.valor) }}
                  </span>
                </td>
                <td>
                  <div class="date-info">
                    <span>{{ formatarData(boleto.vencimento) }}</span>
                    <small v-if="isVencido(boleto.vencimento) && boleto.status_boleto === 'Emitido'" class="vencido-badge">
                      Vencido
                    </small>
                  </div>
                </td>
                <td>
                  <span class="status-badge" :class="getStatusClass(boleto.status_boleto)">
                    {{ boleto.status_boleto || 'N/A' }}
                  </span>
                </td>
                <td>
                  <span v-if="boleto.data_pagamento">
                    {{ formatarData(boleto.data_pagamento) }}
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <div class="action-buttons">
                    <button
                      v-if="boleto.url_boleto"
                      @click="abrirBoleto(boleto.url_boleto)"
                      class="btn-action btn-view"
                      title="Visualizar Boleto"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      @click="copiarCodigoBarras(boleto.codigo_barras_digitavel)"
                      class="btn-action btn-copy"
                      title="Copiar Código de Barras"
                    >
                      <i class="fas fa-copy"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginação -->
        <div v-if="pagination" class="pagination-container">
          <div class="pagination-info">
            Mostrando {{ pagination.from }} a {{ pagination.to }} de {{ pagination.total }} boletos
          </div>
          <div class="pagination-controls">
            <button 
              @click="carregarBoletos(pagination.current_page - 1)"
              :disabled="!pagination.prev_page_url"
              class="btn btn-sm btn-secondary"
            >
              <i class="fas fa-chevron-left"></i>
              Anterior
            </button>
            <span class="page-info">
              Página {{ pagination.current_page }} de {{ pagination.last_page }}
            </span>
            <button 
              @click="carregarBoletos(pagination.current_page + 1)"
              :disabled="!pagination.next_page_url"
              class="btn btn-sm btn-secondary"
            >
              Próxima
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Prorrogar Vencimento -->
    <div v-if="showModalProrrogar" class="modal-overlay" @click="fecharModalProrrogar">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-calendar-alt"></i>
            Prorrogar Vencimento de Boletos
          </h4>
          <button @click="fecharModalProrrogar" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Concurso</label>
            <select v-model="prorrogarForm.concurso_id" class="form-control" required>
              <option value="">Selecione um concurso</option>
              <option v-for="concurso in concursos" :key="concurso.id" :value="concurso.id">
                {{ concurso.titulo || concurso.prefeitura }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label>Data de Vencimento Atual</label>
            <input 
              v-model="prorrogarForm.vencimento_atual" 
              type="date" 
              class="form-control"
              required
            />
          </div>

          <div class="form-group">
            <label>Nova Data de Vencimento</label>
            <input 
              v-model="prorrogarForm.novo_vencimento" 
              type="date" 
              class="form-control"
              required
            />
          </div>

          <div class="alert alert-info">
            <i class="fas fa-info-circle"></i>
            Todos os boletos emitidos com a data de vencimento atual serão prorrogados para a nova data.
          </div>
        </div>
        <div class="modal-footer">
          <button @click="fecharModalProrrogar" class="btn btn-secondary">
            Cancelar
          </button>
          <button 
            @click="prorrogarBoletos" 
            class="btn btn-warning"
            :disabled="prorrogando || !prorrogarForm.concurso_id || !prorrogarForm.vencimento_atual || !prorrogarForm.novo_vencimento"
          >
            <i class="fas fa-calendar-alt" :class="{ 'fa-spin': prorrogando }"></i>
            {{ prorrogando ? 'Prorrogando...' : 'Prorrogar Boletos' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'BoletosLista',
  
  data() {
    return {
      loading: false,
      boletos: [],
      concursos: [],
      filters: {
        concurso_id: '',
        status: '',
        candidato: ''
      },
      pagination: null,
      estatisticas: {
        total: 0,
        emitidos: 0,
        pagos: 0,
        vencidos: 0,
        valor_total: 0
      },
      acaoConcursoId: '',
      atualizandoPagamentos: false,
      showModalProrrogar: false,
      prorrogando: false,
      prorrogarForm: {
        concurso_id: '',
        vencimento_atual: '',
        novo_vencimento: ''
      },
      searchTimeout: null
    }
  },
  
  
  created() {
    this.carregarBoletos()
    this.carregarConcursos()
  },
  
  methods: {
    async carregarBoletos(page = 1) {
      try {
        this.loading = true
        
        const params = {
          page,
          per_page: 15
        }
        
        if (this.filters.concurso_id) {
          params.concurso_id = this.filters.concurso_id
        }
        
        if (this.filters.status) {
          params.status = this.filters.status
        }
        
        if (this.filters.candidato && this.filters.candidato.trim()) {
          params.candidato = this.filters.candidato.trim()
        }
        
        const { data } = await axios.get('/ps/boleto/admin/listar', { params })
        
        this.boletos = data.data || []
        this.pagination = {
          current_page: data.current_page,
          last_page: data.last_page,
          from: data.from,
          to: data.to,
          total: data.total,
          prev_page_url: data.prev_page_url,
          next_page_url: data.next_page_url
        }
        
        this.calcularEstatisticas()
      } catch (error) {
        console.error('Erro ao carregar boletos:', error)
        this.$sweet.showError('Erro ao carregar boletos')
      } finally {
        this.loading = false
      }
    },
    
    async carregarConcursos() {
      try {
        const { data } = await axios.get('/ps/concurso/admin/todos')
        this.concursos = data || []
      } catch (error) {
        console.error('Erro ao carregar concursos:', error)
      }
    },
    
    calcularEstatisticas() {
      this.estatisticas = {
        total: this.boletos.length,
        emitidos: this.boletos.filter(b => b.status_boleto === 'Emitido').length,
        pagos: this.boletos.filter(b => b.status_boleto === 'Pago').length,
        vencidos: this.boletos.filter(b => this.isVencido(b.vencimento) && b.status_boleto === 'Emitido').length,
        valor_total: this.boletos.reduce((sum, b) => sum + (parseFloat(b.valor) || 0), 0)
      }
    },
    
    async atualizarPagamentos() {
      if (!this.acaoConcursoId) {
        this.$sweet.showError('Selecione um concurso')
        return
      }
      
      try {
        this.atualizandoPagamentos = true
        
        const { data } = await axios.post('/ps/boleto/admin/atualizar-pagamentos', {
          concurso_id: this.acaoConcursoId
        })
        
        this.$sweet.showSuccess(
          `Pagamentos atualizados com sucesso! ${data.total_atualizados || 0} boleto(s) atualizado(s).`
        )
        
        await this.carregarBoletos()
      } catch (error) {
        console.error('Erro ao atualizar pagamentos:', error)
        this.$sweet.showError(error.response?.data?.error || 'Erro ao atualizar pagamentos')
      } finally {
        this.atualizandoPagamentos = false
      }
    },
    
    abrirModalProrrogar() {
      if (!this.acaoConcursoId) {
        this.$sweet.showError('Selecione um concurso')
        return
      }
      
      this.prorrogarForm.concurso_id = this.acaoConcursoId
      this.showModalProrrogar = true
    },
    
    fecharModalProrrogar() {
      this.showModalProrrogar = false
      this.prorrogarForm = {
        concurso_id: '',
        vencimento_atual: '',
        novo_vencimento: ''
      }
    },
    
    async prorrogarBoletos() {
      try {
        this.prorrogando = true
        
        await axios.post('/ps/boleto/admin/prorrogar', {
          concurso_id: this.prorrogarForm.concurso_id,
          vencimento_atual: this.prorrogarForm.vencimento_atual,
          novo_vencimento: this.prorrogarForm.novo_vencimento
        })
        
        this.$sweet.showSuccess('Boletos prorrogados com sucesso!')
        this.fecharModalProrrogar()
        await this.carregarBoletos()
      } catch (error) {
        console.error('Erro ao prorrogar boletos:', error)
        this.$sweet.showError(error.response?.data?.error || 'Erro ao prorrogar boletos')
      } finally {
        this.prorrogando = false
      }
    },
    
    applyFilters() {
      this.carregarBoletos(1)
    },
    
    clearFilters() {
      this.filters = {
        concurso_id: '',
        status: '',
        candidato: ''
      }
      this.carregarBoletos(1)
    },
    
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        // Resetar para primeira página ao buscar
        this.carregarBoletos(1)
      }, 500)
    },
    
    abrirBoleto(url) {
      window.open(url, '_blank')
    },
    
    copiarCodigoBarras(codigo) {
      if (!codigo) {
        this.$sweet.showError('Código de barras não disponível')
        return
      }
      
      navigator.clipboard.writeText(codigo).then(() => {
        this.$sweet.showSuccess('Código de barras copiado!')
      }).catch(() => {
        this.$sweet.showError('Erro ao copiar código de barras')
      })
    },
    
    isVencido(dataVencimento) {
      if (!dataVencimento) return false
      return new Date(dataVencimento) < new Date()
    },
    
    getStatusClass(status) {
      const classes = {
        'Emitido': 'status-emitido',
        'Pago': 'status-pago',
        'Vencido': 'status-vencido',
        'Cancelado': 'status-cancelado'
      }
      return classes[status] || 'status-default'
    },
    
    formatarData(data) {
      if (!data) return '-';
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Para datas ISO com timezone, remover o 'Z' e tratar como local
        let dateString = data;
        if (typeof data === 'string' && data.includes('T')) {
          dateString = dateString.replace('Z', '');
          if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(dateString);
        if (isNaN(dateObj.getTime())) return '-';
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return '-';
      }
    },
    
    formatarMoeda(valor) {
      if (!valor && valor !== 0) return '0,00'
      return parseFloat(valor).toFixed(2).replace('.', ',')
    },
    
    formatarCPF(cpf) {
      if (!cpf) return ''
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    }
  }
}
</script>

<style lang="sass" scoped>
.boletos-lista
  padding: 20px
  background: #f5f5f5
  min-height: 100vh

.page-header
  margin-bottom: 30px
  
  .page-title
    font-size: 28px
    font-weight: 600
    color: #2c3e50
    margin-bottom: 8px
    
    i
      margin-right: 10px
      color: #007bff
      
  .page-subtitle
    color: #6c757d
    font-size: 14px
    margin: 0

.stats-grid
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr))
  gap: 20px
  margin-bottom: 30px

.stat-card
  background: white
  border-radius: 8px
  padding: 20px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  display: flex
  align-items: center
  gap: 15px
  
  .stat-icon
    width: 60px
    height: 60px
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    font-size: 24px
    color: white
    
    &.total
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    &.pending
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%)
    &.approved
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)
    &.denied
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%)
    &.money
      background: linear-gradient(135deg, #30cfd0 0%, #330867 100%)
      
  .stat-content
    flex: 1
    
    h3
      font-size: 24px
      font-weight: 600
      color: #2c3e50
      margin: 0 0 5px 0
      
    p
      font-size: 12px
      color: #6c757d
      margin: 0

.actions-card, .filters-card, .table-card
  background: white
  border-radius: 8px
  padding: 20px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  margin-bottom: 20px

.actions-title
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  margin-bottom: 15px
    
  i
    margin-right: 8px
    color: #007bff

.actions-row
  display: flex
  gap: 15px
  align-items: flex-end
  
  .action-group
    flex: 1
    
    label
      display: block
      font-size: 12px
      font-weight: 600
      color: #495057
      margin-bottom: 5px

.filters-row
  display: flex
  gap: 15px
  align-items: flex-end
  
  .filter-group
    flex: 1
    
    label
      display: block
      font-size: 12px
      font-weight: 600
      color: #495057
      margin-bottom: 5px
      
  .filter-actions
    display: flex
    gap: 10px

.form-control
  width: 100%
  padding: 8px 12px
  border: 1px solid #dee2e6
  border-radius: 4px
  font-size: 14px
  
  &:focus
    outline: none
    border-color: #007bff
    box-shadow: 0 0 0 3px rgba(0,123,255,0.1)

.btn
  padding: 8px 16px
  border: none
  border-radius: 4px
  font-size: 14px
  font-weight: 500
  cursor: pointer
  transition: all 0.3s
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    
  i
    margin-right: 5px
    
  &.btn-primary
    background: #007bff
    color: white
    
    &:hover:not(:disabled)
      background: #0056b3
      
  &.btn-secondary
    background: #6c757d
    color: white
    
    &:hover:not(:disabled)
      background: #545b62
      
  &.btn-warning
    background: #ffc107
    color: #212529
    
    &:hover:not(:disabled)
      background: #e0a800
      
  &.btn-sm
    padding: 6px 12px
    font-size: 12px

.loading-state, .empty-state
  text-align: center
  padding: 60px 20px
  
  i
    font-size: 48px
    color: #dee2e6
    margin-bottom: 15px
    
  p, h3
    color: #6c757d

.table-responsive
  overflow-x: auto

.table
  width: 100%
  border-collapse: collapse
  
  thead
    background: #f8f9fa
    
    th
      padding: 12px
      text-align: left
      font-weight: 600
      font-size: 12px
      color: #495057
      border-bottom: 2px solid #dee2e6
      
  tbody
    tr
      border-bottom: 1px solid #dee2e6
      transition: background 0.2s
      
      &:hover
        background: #f8f9fa
        
    td
      padding: 12px
      font-size: 14px
      color: #495057

.user-info, .concurso-info
  strong
    display: block
    color: #2c3e50
    margin-bottom: 2px
    line-height: 1.3
    
  small
    color: #6c757d
    font-size: 11px
    line-height: 1.2
    max-height: 32px
    overflow: hidden
    text-overflow: ellipsis
    display: -webkit-box
    -webkit-line-clamp: 2
    -webkit-box-orient: vertical

.status-badge
  display: inline-block
  padding: 4px 12px
  border-radius: 12px
  font-size: 12px
  font-weight: 600
  
  &.status-emitido
    background: #fff3cd
    color: #856404
    
  &.status-pago
    background: #d4edda
    color: #155724
    
  &.status-vencido
    background: #f8d7da
    color: #721c24
    
  &.status-cancelado
    background: #e2e3e5
    color: #383d41

.valor-badge
  font-weight: 600
  color: #28a745

.date-info
  .vencido-badge
    display: block
    color: #dc3545
    font-size: 11px
    margin-top: 4px

.action-buttons
  display: flex
  gap: 5px
  justify-content: center

.btn-action
  width: 32px
  height: 32px
  border: none
  border-radius: 4px
  cursor: pointer
  display: inline-flex
  align-items: center
  justify-content: center
  transition: all 0.2s
  
  &.btn-view
    background: #17a2b8
    color: white
    
    &:hover
      background: #138496
      
  &.btn-copy
    background: #6c757d
    color: white
    
    &:hover
      background: #5a6268

.pagination-container
  display: flex
  justify-content: space-between
  align-items: center
  margin-top: 20px
  padding-top: 20px
  border-top: 1px solid #dee2e6
  
  .pagination-info
    color: #6c757d
    font-size: 14px
    
  .pagination-controls
    display: flex
    gap: 15px
    align-items: center
    
    .page-info
      color: #495057
      font-size: 14px

.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0,0,0,0.5)
  display: flex
  align-items: center
  justify-content: center
  z-index: 1050

.modal-content
  background: white
  border-radius: 8px
  width: 90%
  max-width: 500px
  max-height: 90vh
  overflow: auto

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px
  border-bottom: 1px solid #dee2e6
  
  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50
    
  .modal-close
    background: none
    border: none
    font-size: 20px
    color: #6c757d
    cursor: pointer
    padding: 5px
    border-radius: 50%
    width: 30px
    height: 30px
    display: flex
    align-items: center
    justify-content: center
    
    &:hover
      background: #f8f9fa

.modal-body
  padding: 20px
  
  .form-group
    margin-bottom: 20px
    
    label
      display: block
      font-size: 14px
      font-weight: 600
      color: #495057
      margin-bottom: 8px
      
  .alert
    padding: 12px
    border-radius: 4px
    background: #d1ecf1
    border: 1px solid #bee5eb
    color: #0c5460
    font-size: 13px
    
    i
      margin-right: 8px

.modal-footer
  display: flex
  justify-content: flex-end
  gap: 10px
  padding: 20px
  border-top: 1px solid #dee2e6

.text-center
  text-align: center

.text-muted
  color: #6c757d
</style>

